import React, { useState, useEffect } from 'react';
import './Contact.css';

// Normalise API base so accidental quotes or whitespace in env vars
// (e.g. "\"\"" from hosting panels) don't break URLs.
const RAW_API_BASE = process.env.REACT_APP_API_BASE;
const API_BASE = RAW_API_BASE
  ? RAW_API_BASE.replace(/^['"]+|['"]+$/g, '').trim()
  : '';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState(null);
  const [errors, setErrors] = useState({});

  // Auto-hide notifications: success after 5s, error after 10s
  useEffect(() => {
    if (!submitMsg) return;
    const duration = submitMsg.type === 'success' ? 5000 : 10000;
    const t = setTimeout(() => {
      setSubmitMsg(null);
    }, duration);
    return () => clearTimeout(t);
  }, [submitMsg]);

  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        return !value || value.trim().length < 2 || value.trim().length > 100
          ? 'Name must be between 2 and 100 characters.'
          : '';
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim())
          ? ''
          : 'Please enter a valid email address.';
      case 'phone': {
        const digits = (value || '').replace(/\D/g, '');
        return digits.length < 10 || digits.length > 15
          ? 'Please enter a valid phone number (10-15 digits).'
          : '';
      }
      case 'service':
        return !value ? 'Please select a service.' : '';
      case 'message':
        return !value || value.trim().length < 10 || value.trim().length > 2000
          ? 'Please provide a brief message between 10 and 2000 characters.'
          : '';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Validate the changed field and clear its error instantly if fixed
    const err = validateField(name, value);
    setErrors(prev => {
      const next = { ...prev };
      if (err) next[name] = err; else delete next[name];
      return next;
    });
    // If user is rectifying errors, clear the banner immediately
    setSubmitMsg(prev => {
      if (!prev || prev.type !== 'error') return prev;
      // Clear banner upon any successful field fix
      return err ? prev : null;
    });
  };

  const validate = () => {
    const nextErrors = {};
    // Name: required, min 2 chars
    if (!formData.name || formData.name.trim().length < 2 || formData.name.trim().length > 100) {
      nextErrors.name = 'Name must be between 2 and 100 characters.';
    }
    // Email: basic pattern
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((formData.email || '').trim());
    if (!emailOk) {
      nextErrors.email = 'Please enter a valid email address.';
    }
    // Phone: 10-15 digits after stripping non-digits
    const digits = (formData.phone || '').replace(/\D/g, '');
    if (digits.length < 10 || digits.length > 15) {
      nextErrors.phone = 'Please enter a valid phone number (10-15 digits).';
    }
    // Service: required
    if (!formData.service) {
      nextErrors.service = 'Please select a service.';
    }
    // Message: optional but recommend at least 10 chars for context
    if (!formData.message || formData.message.trim().length < 10 || formData.message.trim().length > 2000) {
      nextErrors.message = 'Please provide a brief message between 10 and 2000 characters.';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitMsg(null);
    if (!validate()) {
      setSubmitMsg({ type: 'error', text: 'Please fix the highlighted fields.' });
      return;
    }
    setSubmitting(true);
    try {
      const base = API_BASE.replace(/\/+$/, '');
      const url = `${base}/api/contact`;

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone,
          service: formData.service,
          message: formData.message.trim(),
        }),
      });
      const data = await res.json().catch(() => ({ error: 'Unexpected response' }));
      if (!res.ok) {
        // Map server-side field validation errors back into the form if present
        if (data && data.fields && typeof data.fields === 'object') {
          setErrors(prev => ({ ...prev, ...data.fields }));
        }
        const errText = data?.error || `Failed to send (status ${res.status})`;
        setSubmitMsg({ type: 'error', text: errText });
        setSubmitting(false);
        return;
      }
      setSubmitMsg({ type: 'success', text: `Thanks ${formData.name}! Your request has been sent.` });
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      setSubmitting(false);
    } catch (err) {
      console.error(err);
      if (err.name === 'AbortError') {
        setSubmitMsg({
          type: 'error',
          text: 'The request is taking longer than expected. Please check your internet connection and try again, or contact us directly by phone/email.',
        });
      } else {
        setSubmitMsg({ type: 'error', text: 'Sorry, something went wrong. Please try again later.' });
      }
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact">
      <div className="container">
        <div className="section-header">
          <h2>Get In Touch</h2>
          <p>Let's discuss how we can help your business grow</p>
        </div>
        <div className="contact-content">
          <div className="contact-info">
            <div className="info-card">
              <div className="info-icon">
                <i className="fas fa-phone-alt"></i>
              </div>
              <div>
                <h4>Call Us</h4>
                <p>+91 7033271030</p>
              </div>
            </div>
            <div className="info-card">
              <div className="info-icon">
                <i className="fas fa-envelope"></i>
              </div>
              <div>
                <h4>Email Us</h4>
                <p>support@nvconsulting.in</p>
              </div>
            </div>
            <div className="info-card">
              <div className="info-icon">
                <i className="fas fa-map-marker-alt"></i>
              </div>
              <div>
                <h4>Visit Us</h4>
                <p>Near Ratanpur Sahayak Thana, Ratanpur Middle School Road,</p>
                <p>Begusarai, Bihar 851101</p>
              </div>
            </div>
            <div className="info-card">
              <div className="info-icon">
                <i className="fas fa-clock"></i>
              </div>
              <div>
                <h4>Working Hours</h4>
                <p>Mon - Sat: 9:00 AM - 7:00 PM</p>
                <p>Sunday: Closed</p>
              </div>
            </div>
          </div>
          <div className="contact-form">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name *"
                  required
                />
                {errors.name && <div className="field-error">{errors.name}</div>}
              </div>
              <div className="form-group">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Your Email *"
                  required
                />
                {errors.email && <div className="field-error">{errors.email}</div>}
              </div>
              <div className="form-group">
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Your Phone *"
                  required
                />
                {errors.phone && <div className="field-error">{errors.phone}</div>}
              </div>
              <div className="form-group">
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Service *</option>
                  <option value="gst">GST Registration</option>
                  <option value="company">Company Registration</option>
                  <option value="itr">Income Tax Filing</option>
                  <option value="trademark">Trademark Registration</option>
                  <option value="other">Other Services</option>
                </select>
                {errors.service && <div className="field-error">{errors.service}</div>}
              </div>
              <div className="form-group">
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Your Message"
                ></textarea>
                {errors.message && <div className="field-error">{errors.message}</div>}
              </div>
              {submitMsg && (
                <div className={`submit-msg ${submitMsg.type}`}>{submitMsg.text}</div>
              )}
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Sending…' : (
                  <>
                    <i className="fas fa-paper-plane"></i> Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
